/* eslint-disable @reatom/unit-naming-rule */
import {
  atom,
  onConnect,
  reaction,
  reatomAsync,
  sleep,
  withAbort,
} from '@reatom/framework'

import { AsyncStateN, Result } from '@blade/std-lib'

import { pickBestVariant } from '../services/pathfinder-swap-service'

import type {
  PathfinderError,
  PathfinderSwapService,
} from '../services/pathfinder-swap-service'
import type { QuoteResponseBody } from '@blade/pathfinder-api-client'
import type { Atom } from '@reatom/framework'

const REFETCH_DEBOUNCE_MS = 250
const SOL_LAMPORTS_PER_SOL = 1_000_000_000

export const PATHFINDER_DEBUG_PRESETS_SOL = [0.01, 0.02, 0.5, 1] as const

export const pathfinderDebugOpenAtom = atom(false, 'pathfinderDebugOpenAtom')

export type PresetCachedQuote = {
  quote: QuoteResponseBody
  fetchedAt: number
  latencyMs: number
}

export type PresetQuoteState = AsyncStateN.AsyncStateN<
  PathfinderError,
  PresetCachedQuote
>

const solToLamports = (sol: number): bigint =>
  BigInt(Math.trunc(sol * SOL_LAMPORTS_PER_SOL))

export interface PathfinderPresetQuotesInputs {
  presetsAtom: Atom<ReadonlyArray<number>>
  serviceAtom: Atom<PathfinderSwapService | undefined>
  tokenInAtom: Atom<string | undefined>
  tokenOutAtom: Atom<string | undefined>
  takerAtom: Atom<string | undefined>
  name?: string
}

export const reatomPathfinderPresetQuotes = (
  inputs: PathfinderPresetQuotesInputs,
) => {
  const name = inputs.name ?? 'pathfinder-preset-quotes'

  const statesAtom = atom<Map<number, PresetQuoteState>>(
    new Map(),
    `${name}.states`,
  )

  const refreshAction = reatomAsync(async (ctx) => {
    const service = ctx.get(inputs.serviceAtom)
    const tokenIn = ctx.get(inputs.tokenInAtom)
    const tokenOut = ctx.get(inputs.tokenOutAtom)
    const taker = ctx.get(inputs.takerAtom)
    const presets = ctx.get(inputs.presetsAtom)

    if (!service || !tokenIn || !tokenOut || !taker || presets.length === 0) {
      statesAtom(ctx, new Map())
      return
    }

    statesAtom(ctx, () => {
      const next = new Map<number, PresetQuoteState>()
      for (const sol of presets) {
        next.set(sol, AsyncStateN.loading())
      }
      return next
    })

    await ctx.schedule(() =>
      Promise.all(
        presets.map(async (sol) => {
          const t0 = performance.now()
          const result = await ctx.schedule(() =>
            service.quote({
              tokenIn,
              tokenOut,
              amountInRaw: solToLamports(sol),
              taker,
            }),
          )
          const elapsed = performance.now() - t0
          const next: PresetQuoteState = Result.isSuccess(result)
            ? AsyncStateN.success({
                quote: result.success,
                fetchedAt: Date.now(),
                latencyMs: elapsed,
              })
            : AsyncStateN.error(result.failure)
          statesAtom(ctx, (prev) => {
            const merged = new Map(prev)
            merged.set(sol, next)
            return merged
          })
        }),
      ),
    )
  }, `${name}.refresh`).pipe(withAbort())

  const inputsReaction = reaction(async (ctx) => {
    refreshAction.abort(ctx, 'inputs-changed')
    ctx.spy(inputs.tokenInAtom)
    ctx.spy(inputs.tokenOutAtom)
    ctx.spy(inputs.takerAtom)
    ctx.spy(inputs.presetsAtom)
    ctx.spy(inputs.serviceAtom)

    await ctx.schedule(() => sleep(REFETCH_DEBOUNCE_MS))
    refreshAction(ctx)
  }, `${name}.reaction`)

  onConnect(statesAtom, (ctx) => {
    const sub = inputsReaction(ctx)
    refreshAction(ctx)
    return () => {
      refreshAction.abort(ctx, 'disconnect')
      sub.unsubscribe()
    }
  })

  return {
    statesAtom,
    refreshAction,
  }
}

export type PathfinderPresetQuotesModel = ReturnType<
  typeof reatomPathfinderPresetQuotes
>

export const summarizeQuoteForCard = (quote: QuoteResponseBody) => {
  const variant = pickBestVariant(quote)
  if (!variant) {
    return undefined
  }
  const hops = variant.routes[0]?.hops.length ?? 0
  return {
    hops,
    amountOut: variant.total_amount_out,
    priceImpactBps: variant.price_impact_bps,
    transaction: variant.transaction ?? undefined,
  }
}
